/**
 * @fileoverview Balance Sheet Component
 * Professional balance sheet display with proper accounting structure
 */

import React, { useState, useEffect } from 'react';
import { 
  ACCOUNT_CATEGORIES, 
  CATEGORY_DEFINITIONS, 
  getAccountLinesByCategory
} from '../../models/Account';
import './BalanceSheet.css';

/**
 * BalanceSheet Component
 * @param {Object} props - Component props
 * @param {Array} props.financialData - Financial data array
 * @param {string} props.entityName - Entity name for display
 * @param {string} props.currency - Currency code
 * @param {Date} props.asOfDate - Balance sheet date
 */
const BalanceSheet = ({ 
  financialData = [], 
  entityName = "Entity", 
  currency = "USD",
  asOfDate = new Date()
}) => {
  // Date selection state
  const [selectedPeriod, setSelectedPeriod] = useState('current');
  const [effectiveDate, setEffectiveDate] = useState(asOfDate);
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);

  // Calculate period end dates
  const calculatePeriodDates = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const currentQuarter = Math.floor(currentMonth / 3);
    
    // Calculate last quarter end
    let lastQuarterEndMonth, lastQuarterEndYear;
    if (currentQuarter === 0) {
      // If we're in Q1, last quarter end is Q4 of previous year
      lastQuarterEndMonth = 11; // December
      lastQuarterEndYear = currentYear - 1;
    } else {
      // Otherwise, it's the previous quarter of current year
      lastQuarterEndMonth = (currentQuarter - 1) * 3 + 2; // Last month of previous quarter
      lastQuarterEndYear = currentYear;
    }

    return {
      current: new Date(),
      lastMonthEnd: new Date(currentYear, currentMonth, 0), // Last day of previous month
      lastQuarterEnd: new Date(lastQuarterEndYear, lastQuarterEndMonth + 1, 0), // Last day of previous quarter
      lastYearEnd: new Date(currentYear - 1, 11, 31), // December 31 of previous year
    };
  };

  const periodDates = calculatePeriodDates();

  // Handle period selection
  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    setShowCustomDatePicker(false);
    
    if (period === 'custom') {
      setShowCustomDatePicker(true);
    } else {
      setEffectiveDate(periodDates[period] || new Date());
    }
  };

  // Handle custom date change
  const handleCustomDateChange = (event) => {
    const newDate = new Date(event.target.value);
    setEffectiveDate(newDate);
  };

  // Update effective date when asOfDate prop changes
  useEffect(() => {
    if (selectedPeriod === 'current') {
      setEffectiveDate(asOfDate);
    }
  }, [asOfDate, selectedPeriod]);

  // Group financial data by account line ID
  const dataByAccountLine = {};
  financialData.forEach(data => {
    dataByAccountLine[data.accountLineId] = data;
  });

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === 0) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Math.abs(amount));
  };

  // Format date
  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(new Date(date));
  };

  // Calculate category totals
  const calculateCategoryTotal = (categoryType) => {
    const accountLines = getAccountLinesByCategory(categoryType);
    return accountLines.reduce((total, line) => {
      const data = dataByAccountLine[line.id];
      if (data && data.balance !== undefined) {
        return total + Math.abs(data.balance);
      }
      return total;
    }, 0);
  };

  // Render account lines for a category
  const renderAccountLines = (categoryType) => {
    const accountLines = getAccountLinesByCategory(categoryType);
    const categoryDef = CATEGORY_DEFINITIONS[categoryType];
    
    if (accountLines.length === 0) return null;

    return (
      <div className="balance-sheet-category">
        <div className="category-header">
          <span className="category-icon">{categoryDef.icon}</span>
          <h3 className="category-title">{categoryDef.name}</h3>
        </div>
        
        <div className="account-lines">
          {accountLines.map(line => {
            const data = dataByAccountLine[line.id];
            const balance = data?.balance || 0;
            const previousBalance = data?.previousBalance || 0;
            const change = balance - previousBalance;
            const changePercent = previousBalance !== 0 ? (change / Math.abs(previousBalance)) * 100 : 0;
            
            if (Math.abs(balance) < 1) return null; // Skip zero balances
            
            return (
              <div key={line.id} className="account-line">
                <div className="account-info">
                  <span className="account-name">{line.name}</span>
                  <span className="account-code">{line.code}</span>
                </div>
                <div className="account-amounts">
                  <div className="current-amount">
                    {formatCurrency(balance)}
                  </div>
                  {change !== 0 && (
                    <div className={`change-indicator ${change > 0 ? 'positive' : 'negative'}`}>
                      {change > 0 ? '+' : ''}{formatCurrency(change)}
                      {Math.abs(changePercent) > 0.1 && (
                        <span className="change-percent">
                          ({change > 0 ? '+' : ''}{changePercent.toFixed(1)}%)
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="category-total">
          <span className="total-label">Total {categoryDef.name}</span>
          <span className="total-amount">{formatCurrency(calculateCategoryTotal(categoryType))}</span>
        </div>
      </div>
    );
  };

  // Calculate totals for validation
  const totalAssets = calculateCategoryTotal(ACCOUNT_CATEGORIES.ASSETS);
  const totalLiabilities = calculateCategoryTotal(ACCOUNT_CATEGORIES.LIABILITIES);
  const totalEquity = calculateCategoryTotal(ACCOUNT_CATEGORIES.EQUITY);
  const totalLiabilitiesAndEquity = totalLiabilities + totalEquity;
  const isBalanced = Math.abs(totalAssets - totalLiabilitiesAndEquity) < 1; // Allow $1 rounding difference

  // Calculate balance sheet financial ratios
  const calculateRatios = () => {
    if (totalAssets === 0) {
      return {
        debtToAssets: 0,
        equityRatio: 0,
        debtToEquity: 0
      };
    }

    const debtToAssets = (totalLiabilities / totalAssets) * 100;
    const equityRatio = (totalEquity / totalAssets) * 100;
    const debtToEquity = totalEquity > 0 ? (totalLiabilities / totalEquity) * 100 : 0;

    return {
      debtToAssets: debtToAssets.toFixed(1),
      equityRatio: equityRatio.toFixed(1),
      debtToEquity: debtToEquity.toFixed(1)
    };
  };

  const ratios = calculateRatios();

  // Get ratio status and color
  const getRatioStatus = (ratio, value) => {
    const numValue = parseFloat(value);
    switch (ratio) {
      case 'debtToAssets':
        if (numValue < 30) return { status: 'Excellent', color: '#10b981' };
        if (numValue < 50) return { status: 'Good', color: '#f59e0b' };
        return { status: 'High Risk', color: '#ef4444' };
      case 'equityRatio':
        if (numValue > 50) return { status: 'Strong', color: '#10b981' };
        if (numValue > 30) return { status: 'Moderate', color: '#f59e0b' };
        return { status: 'Weak', color: '#ef4444' };
      case 'debtToEquity':
        if (numValue < 50) return { status: 'Conservative', color: '#10b981' };
        if (numValue < 100) return { status: 'Balanced', color: '#f59e0b' };
        return { status: 'Aggressive', color: '#ef4444' };
      default:
        return { status: '', color: '#6b7280' };
    }
  };

  return (
    <div className="balance-sheet">
      <div className="balance-sheet-header">
        <div className="header-left">
          <h2 className="statement-title">Balance Sheet</h2>
          <div className="statement-info">
            <h3 className="entity-name">{entityName}</h3>
            <p className="as-of-date">As of {formatDate(effectiveDate)}</p>
          </div>
        </div>
        
        <div className="header-right">
          <div className="period-selector">
            <div className="period-buttons">
              <button 
                className={`period-btn ${selectedPeriod === 'current' ? 'active' : ''}`}
                onClick={() => handlePeriodChange('current')}
              >
                Current
              </button>
              <button 
                className={`period-btn ${selectedPeriod === 'lastMonthEnd' ? 'active' : ''}`}
                onClick={() => handlePeriodChange('lastMonthEnd')}
              >
                Month End
              </button>
              <button 
                className={`period-btn ${selectedPeriod === 'lastQuarterEnd' ? 'active' : ''}`}
                onClick={() => handlePeriodChange('lastQuarterEnd')}
              >
                Quarter End
              </button>
              <button 
                className={`period-btn ${selectedPeriod === 'lastYearEnd' ? 'active' : ''}`}
                onClick={() => handlePeriodChange('lastYearEnd')}
              >
                Year End
              </button>
              <button 
                className={`period-btn ${selectedPeriod === 'custom' ? 'active' : ''}`}
                onClick={() => handlePeriodChange('custom')}
              >
                Custom
              </button>
            </div>
            
            {showCustomDatePicker && (
              <div className="custom-date-picker">
                <input
                  type="date"
                  value={effectiveDate.toISOString().split('T')[0]}
                  onChange={handleCustomDateChange}
                  className="custom-date-input"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="balance-sheet-content">
        <div className="assets-section">
          {renderAccountLines(ACCOUNT_CATEGORIES.ASSETS)}
        </div>

        <div className="liabilities-section">
          {renderAccountLines(ACCOUNT_CATEGORIES.LIABILITIES)}
        </div>

        <div className="equity-section">
          {renderAccountLines(ACCOUNT_CATEGORIES.EQUITY)}
        </div>
      </div>

      <div className="balance-sheet-summary">
        <div className="summary-boxes">
          <div className="summary-box debt-ratio">
            <div className="summary-label">Debt-to-Assets Ratio</div>
            <div className="summary-amount">{ratios.debtToAssets}%</div>
            <div className="ratio-status" style={{ color: getRatioStatus('debtToAssets', ratios.debtToAssets).color }}>
              {getRatioStatus('debtToAssets', ratios.debtToAssets).status}
            </div>
          </div>
          <div className="summary-box equity-ratio">
            <div className="summary-label">Equity Ratio</div>
            <div className="summary-amount">{ratios.equityRatio}%</div>
            <div className="ratio-status" style={{ color: getRatioStatus('equityRatio', ratios.equityRatio).color }}>
              {getRatioStatus('equityRatio', ratios.equityRatio).status}
            </div>
          </div>
          <div className="summary-box debt-equity-ratio">
            <div className="summary-label">Debt-to-Equity Ratio</div>
            <div className="summary-amount">{ratios.debtToEquity}%</div>
            <div className="ratio-status" style={{ color: getRatioStatus('debtToEquity', ratios.debtToEquity).color }}>
              {getRatioStatus('debtToEquity', ratios.debtToEquity).status}
            </div>
          </div>
        </div>
        <div className={`balance-check ${isBalanced ? 'balanced' : 'unbalanced'}`}>
          <span className="balance-status">
            {isBalanced ? '✓ Balance Sheet is balanced' : '⚠ Balance Sheet does not balance'}
          </span>
          {!isBalanced && (
            <span className="balance-difference">
              Difference: {formatCurrency(totalAssets - totalLiabilitiesAndEquity)}
            </span>
          )}
        </div>
      </div>

      {financialData.length === 0 && (
        <div className="no-data-message">
          <p>No financial data available for this entity.</p>
          <p>Connect data sources or manually enter account balances to view the balance sheet.</p>
        </div>
      )}
    </div>
  );
};

export default BalanceSheet; 